import { FocusEvent, memo, ReactNode, useCallback, useMemo } from "react";
import classnames from "classnames";
import { DropEvent, DropzoneOptions, FileRejection, useDropzone } from "react-dropzone";

import { ReactComponent as LogoIcon } from "./icons/logo.svg";

import styles from "./style.module.scss";

export type FileUploadFieldProps = DropzoneOptions & {
  onChange?: (files: File | File[] | null) => void,
  value?: File | File[] | null,
  className?: string,
  inputClassName?: string,
  iconClassName?: string,
  titleClassName?: string,
  subtitleClassName?: string,
  messageClassName?: string,
  errorClassName?: string,
  fileNameClassName?: string,
  onBlur?: (e: FocusEvent<HTMLDivElement>) => void,
  showFileNames?: boolean,
  hasError?: boolean,
  appendFiles?: boolean,
  errorText?: string,
  id?: string,
  name?: string
  logo?: ReactNode
}

export const FileUploadField = memo<FileUploadFieldProps>(
  (
    {
      onChange,
      onDrop,
      multiple,
      className,
      inputClassName,
      iconClassName,
      titleClassName,
      subtitleClassName,
      messageClassName,
      errorClassName,
      fileNameClassName,
      id,
      name,
      hasError,
      errorText,
      disabled,
      showFileNames,
      value,
      onBlur,
      appendFiles,
      logo,
      ...props
    }) => {
    const arrayValue = useMemo(() => value ? (Array.isArray(value) ? value : [ value ]) : [], [ value ]);
    const wrappedOnDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
      onDrop?.(acceptedFiles, fileRejections, event);
      onChange?.(multiple
        ? (appendFiles ? [ ...arrayValue, ...acceptedFiles ] : acceptedFiles)
        : acceptedFiles?.[0],
      );
    }, [ onChange, onDrop, multiple, arrayValue, appendFiles ]);
    const { getRootProps, getInputProps, isDragActive, isFocused, isDragAccept, isDragReject } = useDropzone({
      useFsAccessApi: false,
      onDrop: wrappedOnDrop,
      multiple,
      disabled,
      ...props,
    });
    const { onBlur: rootOnBlur, ...rootProps } = getRootProps();
    const inputProps = getInputProps();
    const wrappedOnBlur = useCallback((e: FocusEvent<HTMLDivElement>) => {
      rootOnBlur?.(e);
      onBlur?.(e);
    }, [ rootOnBlur, onBlur ]);

    return (
      <div
        {...rootProps}
        id={id}
        onBlur={wrappedOnBlur}
        className={classnames(className, styles.root, {
          [styles.error]: hasError,
          [styles.reject]: isDragReject,
          [styles.accept]: isDragAccept,
          [styles.disabled]: disabled,
          [styles.focused]: isFocused || isDragActive,
        })}
      >
        <input
          name={name}
          className={classnames(inputClassName, styles.input)}
          {...inputProps}
        />
        <div className={classnames(messageClassName, styles.message)}>
          {
            isDragActive ?
              <>
                <p className={classnames(titleClassName, styles.messageTitle)}>
                  {
                    isDragReject
                      ? "Unsupported files"
                      : "Drop files here ..."
                  }
                </p>
              </>
              :
              <>
                <i className={classnames(iconClassName, styles.messageIcon)}>
                  {logo ? logo : <LogoIcon />}
                </i>
                <p className={classnames(titleClassName, styles.messageTitle)}>Drag and drop files here</p>
                <p className={classnames(subtitleClassName, styles.messageSubTitle)}>or click to select files</p>
              </>
          }
        </div>
        {
          hasError && (
            <span className={classnames(errorClassName, styles.errorText)} title={errorText}>
              {errorText}
            </span>
          )
        }
        {
          (!hasError && !isDragActive && showFileNames && arrayValue.length > 0) && (
            <span
              className={classnames(fileNameClassName, styles.fileName)}
              title={errorText}
            >
              {arrayValue.map(x => x.name)
                .join(", ")}
            </span>
          )
        }
      </div>
    );
  });

export default FileUploadField;
